import {useDroppable, useDndMonitor} from "@dnd-kit/core";
import {MoneyLabel} from "../../../../../../components/general/MoneyLabel.tsx";
import {CheckIcon, XMarkIcon, EllipsisHorizontalCircleIcon} from "@heroicons/react/24/outline";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {DraggableAttachedItem} from "./DraggableAttachedItem.tsx";
import {PartialMatchItem} from "./PartialMatchItem.tsx";
import {AttachedItem, DEFAULT_CURRENCY, Transaction} from "../dndTypes.ts";
import {canMatch, canMatchBillDirections, partitionAttachedIds, partialMatchDifferenceLabel} from "../utils/utils.ts";
import {useState, useMemo} from "react";
import {BillDocument, PartialMatch} from "../types.ts";

interface PartialMatchGroupCardProps {
    partialMatch: PartialMatch;
    attachedItems: AttachedItem[];
    onRemoveItem: (itemId: string) => void;
    onAddParts: (matchId: string, transactionIds: string[], billIds: string[]) => void;
    onReject: () => void;
    activeDraggingId?: string | null;
    activeTransaction?: Transaction | null;
    activeDocument?: BillDocument | null;
    onAttachedDocumentClick?: (docId: string) => void;
    onOpenBillDocument: (businessId: string, documentId: string) => void;
    onUnmatch: (billIds: string[], matchId: string, transactionIds: string[]) => Promise<void>;
    onMarkAsComplete?: (matchId: string, defaultDate: string) => void;
}

function latestPartDate(dates: string[]): string {
    const isoDays = dates.filter((d) => d).map((d) => d.slice(0, 10));
    if (isoDays.length === 0) {
        return new Date().toISOString().slice(0, 10);
    }
    return isoDays.reduce((latest, current) => (current > latest ? current : latest));
}

function DifferenceBadge({
    nrsRemainder,
    billDirection,
    currency,
}: {
    nrsRemainder: number | null;
    billDirection: PartialMatch["bill_direction"];
    currency: string;
}) {
    const label = partialMatchDifferenceLabel(nrsRemainder, billDirection);
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-0.5 text-[12px] font-medium text-white">
            {label.prefix && <span>{label.prefix}</span>}
            {label.showAmount && nrsRemainder != null && (
                <MoneyLabel value={Math.abs(nrsRemainder) / 100} currency={currency} showCurrency={true} />
            )}
            {label.suffix && <span>{label.suffix}</span>}
        </span>
    );
}

export function PartialMatchGroupCard({
    partialMatch,
    attachedItems,
    onRemoveItem,
    onAddParts,
    onReject,
    activeDraggingId = null,
    activeTransaction = null,
    activeDocument = null,
    onAttachedDocumentClick,
    onOpenBillDocument,
    onUnmatch,
    onMarkAsComplete,
}: PartialMatchGroupCardProps) {
    const matchId = partialMatch.match_id;
    const currency = partialMatch.remainder.currency || DEFAULT_CURRENCY;
    const nrsRemainder = partialMatch.nrs_remainder;
    const billDirection = partialMatch.bill_direction;

    const [isIncompatible, setIsIncompatible] = useState(false);
    const [blocksMixedDirections, setBlocksMixedDirections] = useState(false);

    const nrsRunningRemainder = useMemo(() => {
        if (nrsRemainder == null) return null;
        let s = 0;
        for (const item of attachedItems) {
            if (item.nrs_amount == null) return null;
            s += item.nrs_amount;
        }
        return nrsRemainder + s;
    }, [nrsRemainder, attachedItems]);

    const {setNodeRef, isOver} = useDroppable({id: matchId});

    useDndMonitor({
        onDragOver: ({over}) => {
            if (over?.id !== matchId) {
                setIsIncompatible(false);
                setBlocksMixedDirections(false);
                return;
            }

            let draggedCurrency: string | undefined = undefined;
            let draggedNrsAmount: number | null | undefined = undefined;
            let mixedDirections = false;

            if (activeTransaction) {
                draggedCurrency = activeTransaction.total_amount_payable.currency ?? undefined;
                draggedNrsAmount = activeTransaction.nrs_amount ?? null;
            } else if (activeDocument?.scanned_fields) {
                draggedCurrency = activeDocument.scanned_fields.total_amount_payable.currency ?? undefined;
                draggedNrsAmount = activeDocument.nrs_amount ?? null;
                mixedDirections = !canMatchBillDirections(
                    [
                        ...partialMatch.bills.map((bill) => bill.document_direction),
                        ...attachedItems.filter((item) => item.kind === 'bill').map((item) => item.document_direction),
                    ],
                    activeDocument.document_direction,
                );
            }

            if (draggedNrsAmount !== undefined) {
                const compatible = canMatch(currency, draggedCurrency, nrsRemainder, draggedNrsAmount) && !mixedDirections;
                setIsIncompatible(!compatible);
                setBlocksMixedDirections(mixedDirections);
            }
        },
        onDragEnd: () => {
            setIsIncompatible(false);
            setBlocksMixedDirections(false);
        },
    });

    const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
        e.stopPropagation();
    };

    const handleAcceptClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const {billIds, transactionIds} = partitionAttachedIds(attachedItems);
        onAddParts(matchId, transactionIds, billIds);
    };

    const handleMarkAsComplete = () => {
        const dates = [
            ...partialMatch.transactions.map((t) => t.booking_date),
            ...partialMatch.bills.map((b) => b.normalized_date),
        ];
        onMarkAsComplete?.(matchId, latestPartDate(dates));
    };

    const cardClassName = [
        "p-3 rounded-md bg-slate-50 mb-2 transition border border-slate-200 relative flex flex-col gap-3 cursor-default",
        isOver && isIncompatible && "outline-2 outline-dashed outline-red-500 outline-offset-[-2px] bg-red-50",
        isOver && !isIncompatible && "outline-2 outline-dashed outline-emerald-500 outline-offset-[-2px] bg-emerald-50",
    ].filter(Boolean).join(" ");

    return (
        <div ref={setNodeRef} className={cardClassName}>
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="shrink-0 inline-flex items-center rounded-full px-2.5 py-[0.1rem] text-xs font-medium bg-slate-200 text-slate-700">
                        Deelmatch
                    </span>
                    <DifferenceBadge nrsRemainder={nrsRemainder} billDirection={billDirection} currency={currency} />
                </div>

                <Menu as="div" className="relative flex-shrink-0">
                    <MenuButton className="inline-flex items-center text-gray-400 hover:text-gray-600 focus:outline-none">
                        <EllipsisHorizontalCircleIcon className="h-5 w-5" aria-hidden="true" />
                    </MenuButton>
                    <MenuItems
                        transition
                        anchor="bottom end"
                        className="z-10 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none [--anchor-gap:0.5rem] data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                    >
                        <div className="py-1">
                            <MenuItem>
                                {({focus}) => (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkAsComplete();
                                        }}
                                        className={`${focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                    >
                                        Verschil wegboeken
                                    </button>
                                )}
                            </MenuItem>
                        </div>
                    </MenuItems>
                </Menu>
            </div>

            <div className="space-y-2">
                {partialMatch.transactions.map((tx) => (
                    <PartialMatchItem
                        key={tx.transaction_id}
                        transaction={tx}
                        currency={currency}
                        onDelete={() => onUnmatch([], matchId, [tx.transaction_id])}
                    />
                ))}
                {partialMatch.bills.map((bill) => (
                    <PartialMatchItem
                        key={bill.bill_id}
                        bill={bill}
                        currency={currency}
                        onDelete={() => onUnmatch([bill.bill_id], matchId, [])}
                        onOpenDocument={() => onOpenBillDocument(bill.business_id, bill.document_id)}
                    />
                ))}
                {attachedItems.map((item) => (
                    <DraggableAttachedItem
                        key={item.id}
                        item={item}
                        currency={currency}
                        onRemove={() => onRemoveItem(item.id)}
                        isDragging={activeDraggingId === item.id}
                        onOpenDocument={onAttachedDocumentClick}
                    />
                ))}
                {isOver && blocksMixedDirections && (
                    <p className="text-sm text-red-700">Een verkoopfactuur en een inkoopfactuur kunnen niet in dezelfde match. Match verkoop met ontvangsten, inkoop met betalingen.</p>
                )}
            </div>

            {attachedItems.length > 0 && (
                <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                        <DifferenceBadge nrsRemainder={nrsRunningRemainder} billDirection={billDirection} currency={currency} />
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-700">
                        <span>Wil je deze transacties en documenten toevoegen?</span>
                        <button
                            type="button"
                            className="p-2 rounded-full hover:bg-green-50 text-green-600 border border-green-200 transition-colors"
                            onClick={handleAcceptClick}
                            onPointerDown={stopPropagation}
                            onMouseDown={stopPropagation}
                            onMouseUp={stopPropagation}
                            aria-label="Accept match"
                        >
                            <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            className="p-2 rounded-full hover:bg-red-50 text-red-600 border border-red-200 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onReject();
                            }}
                            onPointerDown={stopPropagation}
                            onMouseDown={stopPropagation}
                            onMouseUp={stopPropagation}
                            aria-label="Reject match"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
